import * as React from "react";
import { Button, Dropdown, Option, SpinButton, Switch, Text } from "@fluentui/react-components";
import CommonTooltip from "../../../common/CommonTooltip";
import { XIcon } from "@phosphor-icons/react";
import { useSelector } from "react-redux";
import { lineTypes, useClasses } from "../ShapeMenuItem";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { removeShapeGroup, updateShape, updateShapeGroup } from "../../../../state/cellStylesSlice";
import ColorPickerSubMenu from "../../submenus/ColorPickerSubMenu";
import PropTypes from "prop-types";
import { onSelection } from "../../../../helpers/selection";

const MultiCellShapeSubMenuItem = ({ currentStyle }) => {
  const classes = useClasses();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const structure = useSelector((state) => state.gridd.present.structure);
  const selection = useSelector((state) => state.selection);
  const currentShapeGroup = useSelector((state) =>
    state.gridd.present.cellStyles.shapeGroups
      ? state.gridd.present.cellStyles.shapeGroups[
          state.gridd.present.cellStyles[state.selection.cells[0]?.id]?.shapeGroupId
        ]
      : null
  );
  const currentStyleSheet = useSelector(
    (state) => state.styleSheet.sheets[state.styleSheet.selectedSheet] ?? state.styleSheet.sheets["default"]
  );

  return (
    <div style={{ display: "grid" }}>
      <div
        style={{ display: "grid", gridTemplateColumns: "auto 1fr", gridTemplateRows: "auto auto auto auto", gap: 4 }}
      >
        <Text style={{ paddingLeft: 8, fontSize: 12, fontWeight: 600 }}>{t("menu.shape.fill")}</Text>
        <Text style={{ paddingLeft: 8, fontSize: 12, fontWeight: 600 }}>{t("menu.shape.outline")}</Text>

        <div style={{ justifyContent: "start" }}>
          <Dropdown
            id="shape_menu_fill_panel_button"
            width={50}
            style={{ minWidth: 0, padding: 0, alignContent: "center", justifyContent: "center" }}
            input={{ style: { width: 24 } }}
            value={<div style={{ width: 15, height: 15, backgroundColor: currentStyle?.shape?.color ?? "#e5e5e5" }} />}
            onOptionSelect={(_, data) => {
              const selectedColor = currentStyleSheet?.colors?.find((c) => c.name === data.optionValue);
              if (selectedColor) {
                onSelection(
                  dispatch,
                  updateShape,
                  { structure, selection },
                  {
                    color: selectedColor.value,
                    colorName: selectedColor.name,
                  }
                );
              }
            }}
          >
            <ColorPickerSubMenu
              id="shape_group_fill"
              color={currentStyle?.shape?.color}
              colorName={currentStyle?.shape?.colorName}
              enabled={["nocolor", "background", "base", "diverging", "sequential", "status"]}
              onNewColor={(newColor, newName) => {
                onSelection(
                  dispatch,
                  updateShape,
                  { structure, selection },
                  {
                    color: newColor,
                    colorName: newName,
                  }
                );
              }}
            />
          </Dropdown>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
          <Dropdown
            width={50}
            style={{ minWidth: 0, padding: 0, alignContent: "center", justifyContent: "center" }}
            input={{ style: { width: 24 } }}
            value={
              lineTypes.find(
                (l) => l.outline === currentStyle?.shape?.outline && l.weight === currentStyle?.shape?.outlineWeight
              )?.icon ?? "None"
            }
            selectedOptions={[
              lineTypes.find(
                (l) => l.outline === currentStyle?.shape?.outline && l.weight === currentStyle?.shape?.outlineWeight
              )?.id,
            ]}
            onOptionSelect={(_, d) => {
              const selected = lineTypes.find((lt) => lt.id === d.optionValue);
              onSelection(
                dispatch,
                updateShape,
                { structure, selection },
                {
                  outline: selected.outline,
                  outlineWeight: selected.weight ?? 1,
                }
              );
            }}
          >
            {lineTypes.map((line) => (
              <Option key={line.id} value={line.id}>
                <div style={{ width: 16, height: 16 }}>{line.icon}</div> {t("menu.shape.outline." + line.id)}
              </Option>
            ))}
          </Dropdown>

          <Dropdown
            id="shape_menu_outline_panel_button"
            disabled={currentStyle?.shape?.outline === "none"}
            positioning={"below-end"}
            width={50}
            style={{ minWidth: 0, padding: 0, alignContent: "center", justifyContent: "center" }}
            input={{ style: { width: 24 } }}
            value={
              <div
                style={{
                  width: 15,
                  height: 15,
                  backgroundColor: currentStyle?.shape?.outlineColor ?? "#e5e5e5",
                }}
              />
            }
            onOptionSelect={(_, data) => {
              const selectedColor = currentStyleSheet?.colors?.find((c) => c.name === data.optionValue);
              if (selectedColor) {
                onSelection(
                  dispatch,
                  updateShape,
                  { structure, selection },
                  {
                    outlineColor: selectedColor.value,
                    outlineColorName: selectedColor.name,
                  }
                );
              }
            }}
          >
            <ColorPickerSubMenu
              id="shape_outline"
              color={currentStyle?.shape?.outlineColor}
              colorName={currentStyle?.shape?.outlineColorName}
              enabled={["background", "base", "diverging", "sequential", "status"]}
              onNewColor={(newColor, newName) => {
                onSelection(
                  dispatch,
                  updateShape,
                  { structure, selection },
                  {
                    outlineColor: newColor,
                    outlineColorName: newName,
                  }
                );
              }}
            />
          </Dropdown>
        </div>
      </div>

      <Text style={{ paddingLeft: 4, fontSize: 12, fontWeight: 400, pointerEvents: "none" }}>
        {t("menu.group.header")}
      </Text>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
        <Text style={{ paddingLeft: 8, fontSize: 12, fontWeight: 600 }}>{t("menu.shape.gap")}</Text>
        <Text style={{ paddingLeft: 8, fontSize: 12, fontWeight: 600 }}>{t("menu.shape.margin")}</Text>
        <CommonTooltip content={t("menu.shape.gap")} relationship="label">
          <div style={{ alignContent: "center" }}>
            <SpinButton
              size="small"
              style={{ minWidth: 0, width: 50 }}
              min={0}
              max={32}
              step={1}
              value={currentShapeGroup?.gap ?? 4}
              onChange={(_, d) =>
                dispatch(
                  updateShapeGroup({
                    id: currentStyle.shapeGroupId,
                    gap: isNaN(d.value) ? parseInt(d.displayValue) : d.value,
                  })
                )
              }
            />{" "}
            px
          </div>
        </CommonTooltip>

        <CommonTooltip content={t("menu.shape.margin")} relationship="label">
          <div style={{ alignContent: "center" }}>
            <SpinButton
              size="small"
              style={{ minWidth: 0, width: 50 }}
              min={0}
              max={32}
              step={1}
              value={currentShapeGroup?.margin ?? 4}
              onChange={(_, d) =>
                dispatch(
                  updateShapeGroup({
                    id: currentStyle.shapeGroupId,
                    margin: isNaN(d.value) ? parseInt(d.displayValue) : d.value,
                  })
                )
              }
            />{" "}
            px
          </div>
        </CommonTooltip>
      </div>

      <Text style={{ paddingLeft: 8, fontSize: 12, fontWeight: 600 }}>{t("menu.shape.reverse")}</Text>
      <Switch
        id="shape_menu_reverse_panel_switch"
        checked={currentShapeGroup?.reverse}
        onChange={(e, data) => {
          dispatch(updateShapeGroup({ id: currentStyle.shapeGroupId, reverse: data.checked }));
        }}
      />

      <Button
        id="shape_menu_home_remove_shape_group_panel_button"
        icon={<XIcon />}
        appearance="subtle"
        className={classes.PanelButton}
        style={{ paddingLeft: 0, paddingRight: 4 }}
        onClick={() => {
          dispatch(removeShapeGroup(currentStyle.shapeGroupId));
        }}
      >
        {t("menu.shape.group.delete")}
      </Button>
    </div>
  );
};

MultiCellShapeSubMenuItem.propTypes = {
  currentStyle: PropTypes.object,
};

export default MultiCellShapeSubMenuItem;
